import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from "@mui/material";
import { useContext } from "react";
import { GlobalContext } from "../../GlobalProvider";
import { getEntitiesByAppId, deleteEntityById } from "../../api";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import EntityTypeFilter from '../common/EntityTypeFilter';
import DisplayImage from '../DisplayImage';
import FilterListIcon from '@mui/icons-material/FilterList';
const EntityList = () => {
  const { currentApp } = useContext(GlobalContext); // Get currentApp from GlobalProvider
  const appId = currentApp?.id; // Extract appId from currentApp
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // State for delete confirmation
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState(new Set());
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedSensors, setSelectedSensors] = useState(new Set());

  const fetchEntities = async () => {
    if (!appId) {
      setError("App ID is not set.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getEntitiesByAppId(appId, {
        page: page + 1,
        limit: rowsPerPage,
        entityTypeIds: selectedEntityTypes.size > 0 ? [...selectedEntityTypes].join(',') : undefined,
      });
      setEntities(response.data.data);
      setTotal(response.data.total);
    } catch (err) {
      setError(err.message || "Failed to fetch entities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntities();
  }, [appId, page, rowsPerPage, selectedEntityTypes]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page
  };

  const handleAddEntity = () => {
    navigate("/create-entity"); // Navigate to the Add Entity page
  };

  const handleEditEntity = (entityId) => {
    navigate(`/edit-entity/${entityId}`); // Navigate to the edit entity page
  };

  const handleViewEntity = (entityId) => {
    navigate(`/view-entity/${entityId}`); // Navigate to the view entity page
  }
  const handleViewEntityDef = (entityId) => {
    navigate(`/view-entity-def/${entityId}`); // Navigate to the view entity page
  }

  const handleMenuOpen = (event, entityId) => {
    setAnchorEl(event.currentTarget);
    setSelectedEntityId(entityId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEntityId(null);
  };

  const handleViewEntityWithClose = (entityId) => {
    handleViewEntity(entityId);
    handleMenuClose();
  };

  const handleViewEntityDefWithClose = (entityId) => {
    handleViewEntityDef(entityId);
    handleMenuClose();
  };

  const handleEditEntityWithClose = (entityId) => {
    handleEditEntity(entityId);
    handleMenuClose();
  };

  const handleDeleteWithClose = (entityId) => {
    setDeleteConfirm(entityId);
    handleMenuClose();
  };

  const handleDeleteEntity = async () => {
    try {
      await deleteEntityById(deleteConfirm); // Replace with your API call
      fetchEntities(); // Refresh the data
      setDeleteConfirm(null); // Close the confirmation dialog
    } catch (err) {
      console.error("Failed to delete entity:", err);
    }
  };


  const handleFilterToggle = (entityTypeId) => {
    const newSelected = new Set(selectedEntityTypes);
    if (newSelected.has(entityTypeId)) {
      newSelected.delete(entityTypeId);
    } else {
      newSelected.add(entityTypeId);
    }
    setSelectedEntityTypes(newSelected);
  };

  // Helper function to generate random data based on entity type
  const generateRandomData = (entity) => {
    const entityType = entity.entityTypeId?.name?.toLowerCase() || '';
    const now = new Date();
    
    // Generate random last checked time (mostly within 5 minutes, some up to 1 hour)
    let randomMinutes;
    if (Math.random() > 0.1) { // 90% chance of being within 5 minutes
      randomMinutes = Math.floor(Math.random() * 5) + 1; // 1-5 minutes
    } else {
      randomMinutes = Math.floor(Math.random() * 55) + 6; // 6-60 minutes (rare cases)
    }
    const lastChecked = new Date(now.getTime() - randomMinutes * 60000);
    
    // Determine status based on entity type
    let status = 'Inactive';
    if (entityType.includes('camera') || entityType.includes('sensor') || entityType.includes('traffic')) {
      status = Math.random() > 0.2 ? 'Active' : 'Inactive'; // 80% chance of being active
    } else if (entityType.includes('signal') || entityType.includes('light')) {
      status = Math.random() > 0.1 ? 'Live' : 'Inactive'; // 90% chance of being live
    } else {
      status = Math.random() > 0.3 ? 'Connected' : 'Inactive'; // 70% chance of being connected
    }
    
    // Generate random alerts (0 to 3) but only for 5 or fewer entities
    // We'll use the entity ID to deterministically assign alerts to first 5 entities
    const entityIndex = entities.findIndex(e => e._id === entity._id);
    let activeAlerts = 0;
    if (entityIndex < 5) {
      activeAlerts = Math.floor(Math.random() * 4); // 0-3 alerts for first 5 entities
    }
    // All other entities will have 0 alerts
    
    return {
      lastChecked,
      status,
      activeAlerts
    };
  };

  // Helper function to format time difference
  const formatTimeDifference = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    
    if (diffMins < 1) {
      return `${diffSecs}s ago`;
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else {
      const diffHours = Math.floor(diffMins / 60);
      return `${diffHours}h ago`;
    }
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
      case 'Live':
      case 'Connected':
        return 'success';
      case 'Inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button variant="contained" color="primary" onClick={handleAddEntity}>
              Add Entity
            </Button>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              Filter Types
            </Button>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Keys</TableCell>
                  <TableCell>No of Sensors</TableCell>
                  <TableCell>Last Checked</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Active Alerts</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box textAlign="center" py={3}>
                        <Typography variant="h6" color="textSecondary">
                          {selectedEntityTypes.size > 0 ? "No entities found matching the selected filters" : "No Entities Found"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          {selectedEntityTypes.size > 0 
                            ? "Try adjusting your filters or create a new entity."
                            : "Create your first entity to start managing data for this app."
                          }
                        </Typography>
                        <Button variant="contained" color="primary" onClick={handleAddEntity}>
                          Create New Entity
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  entities.map((entity) => {
                    const randomData = generateRandomData(entity);
                    return (
                      <TableRow key={entity._id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {entity.image?.url && (
                              <DisplayImage url={entity.image.url} name={entity.name} />
                            )}
                            <Typography sx={{ pl: '10px' }}>
                              {entity.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{entity.entityTypeId?.name || "N/A"}</TableCell>
                        <TableCell>
                          {entity.keys.map((key) => (
                            <div key={key._id}>
                              <strong>{key.keyName}:</strong> {key.value}
                            </div>
                          ))}
                        </TableCell>
                        <TableCell>
                          {entity.sensors.length}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {formatTimeDifference(randomData.lastChecked)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={randomData.status}
                            color={getStatusColor(randomData.status)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {randomData.activeAlerts > 0 ? (
                            <Chip
                              label={randomData.activeAlerts}
                              color="error"
                              size="small"
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(event) => handleMenuOpen(event, entity._id)}
                            size="small"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25,50,100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewEntityWithClose(selectedEntityId)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Entity</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleViewEntityDefWithClose(selectedEntityId)}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Definition</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEditEntityWithClose(selectedEntityId)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleDeleteWithClose(selectedEntityId)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon sx={{ color: 'error.main' }} fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this entity? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={handleDeleteEntity}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Drawer */}
      <EntityTypeFilter
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        selectedTypes={selectedEntityTypes}
        onToggleType={handleFilterToggle}
        selectedSensors={selectedSensors}
        onToggleSensor={(sensorId) => {
          const newSelected = new Set(selectedSensors);
          if (newSelected.has(sensorId)) {
            newSelected.delete(sensorId);
          } else {
            newSelected.add(sensorId);
          }
          setSelectedSensors(newSelected);
        }}
      />
    </Box>
  );
};

export default EntityList;
